import {
  Edit,
  TextInput,
  ImageField,
  ImageInput,
  FormDataConsumer,
  CheckboxGroupInput,
  BooleanInput,
  Button,
  ReferenceInput,
  SelectInput,
  NumberInput,
  ArrayInput,
  SimpleFormIterator,
  ReferenceArrayInput,
  SelectArrayInput,
  usePermissions,
  useRecordContext,
  Title,
  useNotify,
  TimeInput,
  useDataProvider,
  AutocompleteArrayInput,
  email,
  useRedirect,
  Confirm,
} from "react-admin";
import { useState } from "react";
import { TabbedForm } from "../common/TabbedForm";
import {
  cuitRegex,
  validateCuit,
  validatePercentageOptional,
  validateQuantity,
  validateRequired,
  validateServiceTypes,
  validateTimeOrder,
  validateTucanMenuAlias,
  validateTucanStoreId,
} from "../utils/Validations";
import { RESTAURANT_SERVICE_TYPES } from "../enums/RestaurantServiceTypes";
import { ROLE_TYPES } from "../enums/Roles";
import { PRODUCT_TYPES } from "../enums/ProductTypes";
import { Box } from "@mui/material";
import {
  GUIDE_RESTAURANT_TYPES,
  RESTAURANT_TYPES,
} from "../enums/RestaurantTypes";
import { IMAGE_MAX_SIZE } from "../constants/FileConstants";
import { PAYMENT_METHODS } from "../enums/PaymentMethods";
import { useFormContext, useWatch } from "react-hook-form";
import { DEFAULT_PURCHASE_SPLIT_PERCENTAGE } from "../constants/PurchaseConstants";
import ImagePreview from "../common/ImagePreview";
import Spacer from "../common/Spacer";
import { formatTimeTimezone, parseTimeTimezone } from "../utils/DateTimeUtils";
import { COUNTRIES } from "../enums/Countries";
import AddressAutocomplete from "../common/AddressAutocomplete.js";

const RestaurantTitle = () => {
  const record = useRecordContext();
  return <Title title={`Restaurante "${record?.name}"`} />;
};

const BillingData = () => {
  const { setValue } = useFormContext();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [loadingBillingData, setLoadingBillingData] = useState(false);

  const resetValues = () => {
    setValue("billingData.docType", "");
    setValue("billingData.name", "");
    setValue("billingData.address", "");
    setValue("billingData.province", "");
    setValue("billingData.taxCondition", "");
  };

  const onBillingDataClick = async (formData) => {
    try {
      const cuit = formData?.billingData?.cuit;

      if (!cuitRegex.test(cuit)) {
        return notify(
          "El CUIT/CUIL no es válido. Debe tener 11 dígitos numéricos y comenzar con un prefijo válido.",
          {
            type: "error",
          }
        );
      }
      setLoadingBillingData(true);
      const {
        data: { name, address, docType, province, taxCondition },
      } = await dataProvider.getBillingDataFromCuit("billing", {
        cuit,
      });

      if (!name || !docType || !taxCondition) {
        notify(
          "No se ha podido obtener la información necesaria para autocompletar los campos de facturación. Por favor, verificar el CUIT/CUIL ingresado o completar manualmente los campos faltantes.",
          {
            type: "error",
          }
        );
        resetValues();
        return;
      }
      setValue("billingData.name", name);
      setValue("billingData.address", address);
      setValue("billingData.docType", docType);
      setValue("billingData.province", province);
      setValue("billingData.taxCondition", taxCondition);

      notify(
        "Información de facturación obtenida exitosamente y campos autocompletados.",
        { type: "success" }
      );
    } catch (error) {
      resetValues();
      notify(
        "Error al obtener los datos de facturación. Por favor, verificar el CUIT/CUIL ingresado o completar manualmente los campos.",
        {
          type: "error",
        }
      );
    } finally {
      setLoadingBillingData(false);
    }
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <>
          <Box sx={{ display: "flex" }}>
            <Box sx={{ flex: 0.4 }}>
              <TextInput
                label="Email"
                source="billingData.email"
                validate={[email()]}
                fullWidth
              />
              <TextInput
                label="CUIT"
                source="billingData.cuit"
                validate={validateCuit}
                fullWidth
              />
              <Button
                label="Buscar datos"
                onClick={() => onBillingDataClick(formData)}
                variant="contained"
                disabled={loadingBillingData}
                style={{
                  marginBottom: "1em",
                }}
              />
              <TextInput
                label="Razón Social"
                source="billingData.name"
                fullWidth
              />
              <TextInput
                label="Domicilio fiscal"
                source="billingData.address"
                fullWidth
              />
              <TextInput
                label="Tipo documento"
                source="billingData.docType"
                fullWidth
              />
              <TextInput
                label="Provincia"
                source="billingData.province"
                fullWidth
              />
              <TextInput
                label="Condición frente al IVA"
                source="billingData.taxCondition"
                fullWidth
              />
            </Box>
          </Box>
        </>
      )}
    </FormDataConsumer>
  );
};

const OpeningHoursRow = () => {
  const country = useWatch({ name: "country" });
  // Relative source: the SimpleFormIterator's SourceContext already resolves
  // to timeTable.<i>.times.<j>
  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <TimeInput
        source="opening"
        label="Apertura"
        validate={validateRequired}
        format={formatTimeTimezone(country)}
        parse={parseTimeTimezone(country)}
        variant="outlined"
        margin="dense"
        style={{ width: 150 }}
      />
      <TimeInput
        source="closing"
        label="Cierre"
        validate={validateRequired}
        format={formatTimeTimezone(country)}
        parse={parseTimeTimezone(country)}
        variant="outlined"
        margin="dense"
        style={{ width: 150 }}
      />
    </Box>
  );
};

const RewardRow = ({ permissions }) => {
  const id = useWatch({ name: "id" });
  // Relative sources: the SimpleFormIterator's SourceContext already
  // resolves to rewards.<i>
  return (
    <Box sx={{ flex: "1" }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <ReferenceInput
          source="product._id"
          reference="products"
          perPage={100}
          sort={{ field: "name", order: "ASC" }}
          filter={{
            restaurant: id,
            type: PRODUCT_TYPES.REWARD,
          }}
        >
          <SelectInput
            optionText="name"
            validate={validateRequired}
            label="Producto"
            readOnly={permissions !== ROLE_TYPES.ADMIN}
          />
        </ReferenceInput>

        <Spacer />

        <NumberInput
          source="necessaryRedemptions"
          label="Canjes Necesarios"
          min={1}
          validate={validateQuantity}
        />

        <Spacer />

        <BooleanInput source="active" label="Activo" defaultValue={true} />
      </Box>
    </Box>
  );
};

const ConditionalDeliveryTab = ({ permissions, ...props }) => {
  const serviceTypes = useWatch({ name: "serviceTypes" });
  if (!serviceTypes?.includes(RESTAURANT_SERVICE_TYPES.DELIVERY)) return null;
  return (
    <TabbedForm.Tab label="Delivery" {...props}>
      <NumberInput
        source="integrations.tucan.storeId"
        label="Store ID en Tucan"
        validate={validateTucanStoreId}
      />
      <TextInput
        label="Menu Alias en Tucan"
        source="integrations.tucan.menuAlias"
        validate={validateTucanMenuAlias}
      />
      {permissions === ROLE_TYPES.ADMIN && (
        <NumberInput
          source="shippingDiscountPercentage"
          label="% Descuento Envío Morfy"
          min={0}
          max={100}
          format={(v) => v * 100}
          parse={(v) => parseFloat(v) / 100}
          validate={validatePercentageOptional}
          onWheel={(e) => e.target.blur()}
          defaultValue={0}
        />
      )}
    </TabbedForm.Tab>
  );
};

const ConditionalGuideProductsTab = (props) => {
  // The live form value, not record.type: the tab has to appear as soon as the
  // admin picks a guide type, before saving
  const type = useWatch({ name: "type" });

  if (!GUIDE_RESTAURANT_TYPES.includes(type)) return null;

  return (
    <TabbedForm.Tab label="Productos (Guía)" {...props}>
      <ImageInput
        source="productImages"
        accept={{
          "image/*": [],
        }}
        label="Imágenes de Productos"
        multiple
        maxSize={IMAGE_MAX_SIZE}
      >
        <ImagePreview source="url" />
      </ImageInput>
    </TabbedForm.Tab>
  );
};

const RestaurantEdit = () => {
  const { permissions } = usePermissions();
  const redirect = useRedirect();
  const [openModal, setOpenModal] = useState(false);
  const [openUnlinkMPModal, setOpenUnlinkMPModal] = useState(false);
  const [deactivatedRestaurant, setDeactivatedRestaurant] = useState(null);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const handleSuccess = (data) => {
    if (data.initialActive === true && data.active === false) {
      setDeactivatedRestaurant(data);
      setOpenModal(true);
    } else {
      redirect("list", "restaurants");
    }
  };

  const handleDeactivateProducts = async () => {
    try {
      const { data } = await dataProvider.deactivateRestaurantProducts(
        "restaurants",
        {
          id: deactivatedRestaurant.id,
        }
      );
      const success = data?.success;
      const successResults = data?.successResults;

      notify(
        `${successResults || 0} productos desactivados con éxito. ${
          !success
            ? "Algunos productos no han sido procesados correctamente"
            : ""
        }`,
        { type: success ? "success" : "warning" }
      );
    } catch (error) {
      notify("Error al desactivar los productos", { type: "error" });
    } finally {
      handleCloseModal();
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);

    if (deactivatedRestaurant?.linkedToMercadoPago) {
      setOpenUnlinkMPModal(true);
    } else {
      redirect("list", "restaurants");
    }
  };

  const handleUnlinkMercadoPago = async () => {
    try {
      await dataProvider.unlinkMercadoPago("restaurants", {
        id: deactivatedRestaurant.id,
      });
      notify("El restaurante ha sido desasociado de Mercado Pago con éxito", {
        type: "success",
      });
    } catch (error) {
      notify(
        "Ha ocurrido un error desasociando el restaurante de Mercado Pago",
        { type: "error" }
      );
    } finally {
      handleCloseUnlinkMPModal();
    }
  };

  const handleCloseUnlinkMPModal = () => {
    setOpenUnlinkMPModal(false);
    redirect("list", "restaurants");
  };

  return (
    <>
      <Edit
        title={<RestaurantTitle />}
        mutationMode="pessimistic"
        redirect="list"
        mutationOptions={{
          onSuccess: handleSuccess,
        }}
      >
        <EditComponent permissions={permissions} />
      </Edit>

      <Confirm
        isOpen={openModal}
        title="Desactivar productos asociados"
        content="Este restaurante fue desactivado. ¿Querés desactivar también todos sus productos?"
        onConfirm={handleDeactivateProducts}
        onClose={handleCloseModal}
        confirm="Sí, desactivar productos"
        cancel="No, mantener activos"
      />

      <Confirm
        isOpen={openUnlinkMPModal}
        title="Desasociar Mercado Pago"
        content="Este restaurante sigue asociado a Mercado Pago. ¿Querés desasociarlo? Si es el dueño de la cuenta, también se desasocian las sucursales que la comparten."
        onConfirm={handleUnlinkMercadoPago}
        onClose={handleCloseUnlinkMPModal}
        confirm="Sí, desasociar"
        cancel="No, mantener asociado"
      />
    </>
  );
};

const EditComponent = ({ permissions }) => {
  const record = useRecordContext();

  // Store initial active status to compare on save
  record.initialActive = record.active;

  return (
    <>
      <TabbedForm>
        <TabbedForm.Tab label="Datos básicos">
          <>
            <Box sx={{ display: "flex" }}>
              <Box sx={{ flex: 0.4, mr: "2em" }}>
                <TextInput
                  source="name"
                  label="Nombre"
                  validate={validateRequired}
                  fullWidth
                  readOnly={permissions !== ROLE_TYPES.ADMIN}
                />
                <TextInput
                  multiline
                  source="description"
                  label="Descripción"
                  validate={validateRequired}
                  fullWidth
                  readOnly={permissions !== ROLE_TYPES.ADMIN}
                />

                <AddressAutocomplete
                  readOnly={permissions !== ROLE_TYPES.ADMIN}
                />

                {permissions === ROLE_TYPES.ADMIN && (
                  <SelectInput
                    label="Tipo"
                    source="type"
                    choices={[
                      { id: RESTAURANT_TYPES.REGULAR, name: "Regular" },
                      { id: RESTAURANT_TYPES.GUIDE, name: "Guía" },
                      { id: RESTAURANT_TYPES.PREGUIDE, name: "Pre-Guía" },
                      { id: RESTAURANT_TYPES.DRAFT, name: "Borrador" },
                    ]}
                  />
                )}

                {permissions === ROLE_TYPES.ADMIN && (
                  <BooleanInput
                    source="active"
                    label="Activo"
                    defaultValue={true}
                    readOnly={record?.type === RESTAURANT_TYPES.DRAFT}
                  />
                )}

                {permissions === ROLE_TYPES.ADMIN && (
                  <BooleanInput
                    source="isOnboarding"
                    label="En onboarding"
                    helperText="Permite al local entrar al backoffice aunque esté inactivo. Acordate de apagarlo cuando termine el onboarding."
                  />
                )}
              </Box>
              <Box sx={{ flex: 0.4 }}>
                <ReferenceArrayInput
                  source="placeTypes"
                  reference="place-types"
                  perPage={100}
                >
                  <SelectArrayInput
                    optionText="name"
                    label="Tipos de Lugar"
                    helperText="Puedes elegir más de un tipo"
                    fullWidth
                  />
                </ReferenceArrayInput>

                <ReferenceArrayInput
                  source="placeTargets"
                  reference="place-targets"
                  perPage={100}
                >
                  <SelectArrayInput
                    optionText="name"
                    label="Target del Lugar"
                    helperText="Puedes elegir más de un target"
                    fullWidth
                  />
                </ReferenceArrayInput>

                <SelectArrayInput
                  label="Métodos de Pago (Cupones)"
                  source="couponPaymentMethods"
                  choices={[
                    {
                      id: PAYMENT_METHODS.CREDIT_CARD,
                      name: "Tarjeta de Crédito",
                    },
                    {
                      id: PAYMENT_METHODS.DEBIT_CARD,
                      name: "Tarjeta de Débito",
                    },
                    { id: PAYMENT_METHODS.MERCADO_PAGO, name: "Mercado Pago" },
                    { id: PAYMENT_METHODS.CASH, name: "Efectivo" },
                  ]}
                  validate={validateRequired}
                  fullWidth
                  readOnly={permissions !== ROLE_TYPES.ADMIN}
                />

                {permissions === ROLE_TYPES.ADMIN && (
                  <SelectArrayInput
                    label="Tipos de Servicio"
                    source="serviceTypes"
                    choices={[
                      { id: RESTAURANT_SERVICE_TYPES.STORE, name: "Local" },
                      {
                        id: RESTAURANT_SERVICE_TYPES.DELIVERY,
                        name: "Delivery",
                      },
                    ]}
                    validate={validateServiceTypes}
                    helperText="El restaurante debe tener al menos un tipo"
                    fullWidth
                  />
                )}

                {permissions === ROLE_TYPES.ADMIN && (
                  <TextInput
                    source="googlePlaceId"
                    label="Google Place ID"
                    fullWidth
                  />
                )}
              </Box>
            </Box>
          </>
        </TabbedForm.Tab>

        {permissions === ROLE_TYPES.ADMIN && (
          <TabbedForm.Tab label="Días y Horarios">
            <ArrayInput source="timeTable" label="">
              <SimpleFormIterator disableReordering>
                <FormDataConsumer>
                  {({ formData }) => {
                    return (
                      <>
                        <CheckboxGroupInput
                          source={"days"}
                          label="Días"
                          choices={[
                            { id: "Mo", name: "Lunes" },
                            { id: "Tu", name: "Martes" },
                            { id: "We", name: "Miércoles" },
                            { id: "Th", name: "Jueves" },
                            { id: "Fr", name: "Viernes" },
                            { id: "Sa", name: "Sábado" },
                            { id: "Su", name: "Domingo" },
                          ]}
                        />
                        <ArrayInput
                          source={"times"}
                          label=""
                          validate={validateTimeOrder}
                        >
                          <SimpleFormIterator disableReordering>
                            <OpeningHoursRow />
                          </SimpleFormIterator>
                        </ArrayInput>
                      </>
                    );
                  }}
                </FormDataConsumer>
              </SimpleFormIterator>
            </ArrayInput>
          </TabbedForm.Tab>
        )}
        {permissions === ROLE_TYPES.ADMIN &&
          record?.country &&
          COUNTRIES[record.country] === COUNTRIES.AR && (
            <TabbedForm.Tab label="Facturación">
              <BillingData />
            </TabbedForm.Tab>
          )}
        {permissions === ROLE_TYPES.ADMIN && (
          <TabbedForm.Tab label="Compras">
            <SelectArrayInput
              label="Métodos de Pago (Compras)"
              source="purchasePaymentMethods"
              choices={[
                {
                  id: PAYMENT_METHODS.CREDIT_CARD,
                  name: "Tarjeta de Crédito",
                },
                {
                  id: PAYMENT_METHODS.DEBIT_CARD,
                  name: "Tarjeta de Débito",
                },
                { id: PAYMENT_METHODS.MERCADO_PAGO, name: "Mercado Pago" },
              ]}
              validate={validateRequired}
              readOnly={permissions !== ROLE_TYPES.ADMIN}
            />
            {permissions === ROLE_TYPES.ADMIN && (
              <NumberInput
                source="purchaseSplitPercentage"
                label="% Fee de Morfy"
                min={0}
                max={100}
                format={(v) => v * 100}
                parse={(v) => parseFloat(v) / 100}
                validate={validatePercentageOptional}
                onWheel={(e) => e.target.blur()}
                defaultValue={DEFAULT_PURCHASE_SPLIT_PERCENTAGE}
              />
            )}
            {permissions === ROLE_TYPES.ADMIN && (
              <BooleanInput
                source="enablePurchases"
                label="Habilitar Compras"
                fullWidth
                helperText="Si está activado, reemplaza los cupones por compras en este restaurante"
              />
            )}
          </TabbedForm.Tab>
        )}

        {permissions === ROLE_TYPES.ADMIN && (
          <ConditionalDeliveryTab permissions={permissions} />
        )}

        {permissions === ROLE_TYPES.ADMIN && (
          <TabbedForm.Tab label="Premios">
            <ArrayInput
              source="rewards"
              label=""
              disabled={permissions !== ROLE_TYPES.ADMIN}
            >
              <SimpleFormIterator disableReordering>
                <RewardRow permissions={permissions} />
              </SimpleFormIterator>
            </ArrayInput>
          </TabbedForm.Tab>
        )}

        <TabbedForm.Tab label="Imágenes">
          <ImageInput
            source="coverImages"
            accept={{
              "image/*": [],
            }}
            label="Imágenes de Cover"
            multiple
            maxSize={IMAGE_MAX_SIZE}
          >
            <ImagePreview source="url" />
          </ImageInput>

          <ImageInput
            source="profileImage"
            label="Imagen de Perfil"
            accept={{
              "image/*": [],
            }}
            maxSize={IMAGE_MAX_SIZE}
          >
            <ImageField source="src" title="Imagen" />
          </ImageInput>
          <FormDataConsumer>
            {({ formData, ...rest }) => {
              if (!formData.profileImage || !formData.profileImage.rawFile) {
                return <ImageField source="profileImage" {...rest} />;
              }
            }}
          </FormDataConsumer>

          <ImageInput
            source="mapImage"
            label="Imagen de Mapa"
            accept={{
              "image/*": [],
            }}
            helperText="Si no subes una imagen, se utilizará la Imagen de Perfil."
            maxSize={IMAGE_MAX_SIZE}
          >
            <ImageField source="src" title="Imagen" />
          </ImageInput>
          <FormDataConsumer>
            {({ formData, ...rest }) => {
              if (!formData.mapImage || !formData.mapImage.rawFile) {
                return <ImageField source="mapImage" {...rest} />;
              }
            }}
          </FormDataConsumer>
        </TabbedForm.Tab>

        {permissions === ROLE_TYPES.ADMIN && (
          <TabbedForm.Tab label="Videos">
            <ReferenceArrayInput
              source="coverVideos"
              reference="videos"
              perPage={100}
              filter={{
                restaurants: [record.id],
              }}
              sort={{ field: "name", order: "ASC" }}
            >
              <AutocompleteArrayInput
                filterToQuery={(searchText) => ({ name: searchText })}
                label="Video(s) de Cover"
                helperText="Puedes elegir más de un video"
                fullWidth
                optionText="name"
              />
            </ReferenceArrayInput>
          </TabbedForm.Tab>
        )}

        {permissions === ROLE_TYPES.ADMIN && <ConditionalGuideProductsTab />}

        {permissions === ROLE_TYPES.ADMIN && (
          <TabbedForm.Tab label="Suscripción">
            <ReferenceInput
              source="subscriptionPlan"
              reference="restaurant-subscription-plans"
              perPage={10000}
            >
              <SelectInput optionText="name" resettable label="Plan" />
            </ReferenceInput>
          </TabbedForm.Tab>
        )}
      </TabbedForm>
    </>
  );
};

export default RestaurantEdit;
